import { HttpErrorResponse } from "@angular/common/http";
import { computed, inject } from "@angular/core";
import { tapResponse } from "@ngrx/operators";
import { patchState, signalStore, withComputed, withMethods, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { EMPTY, from, pipe, switchMap, tap } from "rxjs";

import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from "@application/data-access";
import { ErrorStore } from "@application/errors/data-access";
import { IContract } from "@contract/data-access";
import { PermissionNumber, SelfUserStore } from "@domains/person/user/self/data";

import { ProfileContractService } from "./infra/profile-contract.service";
import { DateTime } from 'luxon';
import { getDateTime } from '@shared/utils';

type ContractStatusKey = "signed" | "signing" | "expired" | "closed";

const AWAITING_STATES = new Set(["signing", "added", "emailtosignsent", "linktosigngenerated"]);

const statusPriority = (status: string | undefined): number => {
  if (!status) return 4;
  if (AWAITING_STATES.has(status)) return 0;
  if (status === "expired") return 1;
  if (status === "signed") return 2;
  if (status === "closed" || status === "declined") return 3;
  return 4;
};

const toTime = (value: string | Date | undefined): number => {
  if (!value) return -Infinity;
  const t = getDateTime(value)?.toMillis();
  return Number.isFinite(t) ? t : -Infinity;
};

const isSignedStatus = (c: IContract): boolean => c.Status === "signed";

/**
 * Meia-noite do dia em que a data cai, no fuso da conta.
 *
 * Era `Date.UTC(...)` sobre um `new Date(value)`, o que dava a meia-noite do dia
 * **UTC** e era depois comparado com `DateTime.now().startOf('day')` — meia-noite
 * **local**. As duas pontas discordavam pelo offset da conta. `getDateTime` lê o
 * valor no fuso da conta, por isso as duas pontas passam a falar do mesmo dia.
 */
const startOfDay = (value: string | Date | undefined): number | null => {
  if (!value) return null;
  const time = getDateTime(value)?.startOf('day').toMillis();
  return Number.isFinite(time) ? time : null;
};

const isInVigencia = (c: IContract): boolean => {
  const today = DateTime.now().startOf('day').toMillis();
  const start = startOfDay(c.StartedOn);
  const finish = startOfDay(c.FinishedOn);
  if (start !== null && start > today) return false;
  if (finish !== null && finish < today) return false;
  return true;
};

export interface IProfileContractStore {
  personId: string | null;
  contractsForPerson: IContract[];
  resendingContractId: string | null;
}

export const initialProfileContractStore: IProfileContractStore = {
  personId: null,
  contractsForPerson: [],
  resendingContractId: null,
};

export const ProfileContractStore = signalStore(
  { providedIn: "root" },
  withState<IProfileContractStore>(initialProfileContractStore),
  withComputed((store) => ({
    latestContract: computed<IContract | null>(() => {
      const contracts = store.contractsForPerson();
      if (!contracts.length) return null;

      const sorted = [...contracts].sort((a, b) => {
        const diff = toTime(b.AddedOn) - toTime(a.AddedOn);
        if (diff !== 0) return diff;
        return statusPriority(a.Status?.split('_')?.[0]) - statusPriority(b.Status?.split('_')?.[0]);
      });

      return sorted[0] ?? null;
    }),

    counts: computed<Record<ContractStatusKey, number>>(() => {
      const acc: Record<ContractStatusKey, number> = {
        signed: 0,
        signing: 0,
        expired: 0,
        closed: 0,
      };
      for (const c of store.contractsForPerson()) {
        const status = c.Status?.split('_')?.[0];
        if (status === "signed") acc.signed++;
        else if (status === "expired") acc.expired++;
        else if (status === "closed") acc.closed++;
        else if (status && AWAITING_STATES.has(status)) acc.signing++;
      }

      return acc;
    }),

    resendInFlight: computed<boolean>(() => store.resendingContractId() !== null),

    blocksVisitorConversion: computed<boolean>(() => {
      const inVigencia = store.contractsForPerson().filter(isInVigencia);
      return inVigencia.length === 0 || !inVigencia.every(isSignedStatus);
    }),
  })),
  withMethods((
    store,
    profileContractService = inject(ProfileContractService),
    errorStore = inject(ErrorStore),
    selfUserStore = inject(SelfUserStore),
  ) => {
    const getByPerson = rxMethod<string | null | undefined>(pipe(
      tap(() =>
        patchState(store, {
          contractsForPerson: [],
          ...setPropLoading("getContractsByPerson"),
        })
      ),
      switchMap((personId) => {
        if (!personId) {
          const error = new Error("Missing personId");
          patchState(store, { ...setPropError("getContractsByPerson", error) });
          return EMPTY;
        }

        return from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.ContratoObterTodos],
            { interactive: false },
          ),
        ).pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, {
                personId,
                contractsForPerson: [],
                ...setPropError("getContractsByPerson", new Error('ALERT.ERROR_401')),
              });
              return EMPTY;
            }

            return profileContractService.getContractsByPerson(personId).pipe(
              tapResponse({
                next: (contractsForPerson) => {
                  patchState(store, {
                    personId,
                    contractsForPerson,
                    ...setPropLoaded("getContractsByPerson"),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { personId, ...setPropError("getContractsByPerson", error) });
                },
              }),
            );
          }),
        );
      }),
    ));

    return {
      reset: () => {
        patchState(
          store,
          { ...initialProfileContractStore },
          setPropInit("getContractsByPerson"),
        );
      },
      setResendingContractId: (contractId: string | null) => {
        patchState(store, { resendingContractId: contractId });
      },
      getByPerson,
    };
  }),
  withReqState("getContractsByPerson"),
);
